import { z } from 'zod';

// Phone regex for Uzbek numbers
export const uzbekPhoneRegex = /^\+998 \(\d{2}\) \d{3} \d{2} \d{2}$/;

const matterBaseSchema = z.object({
  isCourtCase: z.boolean(),
  // Case 360° (Lot 2) Stage 3 — category drives the matter's rules. Required at
  // creation, editable while the matter is in INTAKE, immutable afterwards.
  // MIGRATION_PENDING is accepted only so legacy matters can load in edit mode
  // (never selectable; it is stripped from the update payload when unchanged).
  category: z.enum(
    ['CC', 'CDC', 'ADC', 'ECD', 'CLA', 'PW', 'EBC', 'QA', 'MIGRATION_PENDING'],
    { required_error: 'Category is required', invalid_type_error: 'Category is required' },
  ),
  caseNumber: z.string().optional().default(''),
  plainTiff: z.string().optional().default(''),
  defendant: z.string().optional().default(''),
  description: z.string().optional().default(''),
  judgeName: z.string().optional().default(''),
  courtName: z.string().optional().default(''),
  // Court email/phone formats are validated conditionally below — only for
  // court cases (the fields aren't rendered otherwise, so a legacy value must
  // never silently block submit on a non-court matter).
  courtEmail: z.string().optional().default(''),
  courtPhone: z.string().optional().default(''),
  clientStatus: z.enum(['DEFENDANT', 'PLAINTIFF', 'SUSPECT', 'ACCUSED', 'WITNESS']).default('ACCUSED'),
  contractId: z.number({
    required_error: 'Contract is required',
    invalid_type_error: 'Contract is required',
  }),
  // nullish (not just optional): legacy matters return null for these, and the
  // client combobox writes null when cleared — plain .optional() rejects null
  // with "Expected number, received null".
  clientId: z.number().nullish(),
  practiceAreaId: z.number().nullish(),
  // Case 360° — optional Matter Plan (Stage 11) + initial risk (Stage 6) at
  // creation; editable only while INTAKE on the edit form. Numbers are kept as
  // strings in the form (Input) and converted in the submit payload.
  scopeIn: z.string().max(5000).optional().default(''),
  scopeOut: z.string().max(5000).optional().default(''),
  estimate: z
    .string()
    .regex(/^\d*$/, 'Must be a whole number')
    .optional()
    .default(''),
  budget: z
    .string()
    .regex(/^\d*$/, 'Must be a whole number')
    .optional()
    .default(''),
  targetEndDate: z.string().optional().default(''),
  riskLevel: z.enum(['LOW', 'MEDIUM', 'HIGH']).optional(),
});

export const matterSchema = matterBaseSchema.superRefine((values, ctx) => {
  if (!values.isCourtCase) return;
  if (
    values.courtEmail &&
    !z.string().email().safeParse(values.courtEmail).success
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['courtEmail'],
      message: 'Invalid email',
    });
  }
  if (values.courtPhone && !uzbekPhoneRegex.test(values.courtPhone)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['courtPhone'],
      message: 'Phone must be a valid Uzbek phone number',
    });
  }
});

export type MatterFormValues = z.infer<typeof matterSchema>;

// Close matter schema
export const closeMatterSchema = z.object({
  result: z.enum(['WIN', 'LOSE', 'SETTLED', 'CANCELLED'], {
    required_error: 'Result is required',
  }),
});

export type CloseMatterFormValues = z.infer<typeof closeMatterSchema>;
